import { readItem, readItems } from "@directus/sdk";
import type { Point, Polygon } from "geojson";
import type { OmitAndMerge } from "@/utils/types";
import type { AppDirectusClient } from "../client";
import type { AnyJson, DayOfWeek } from "../model/common";

function collectAccountStepIds(response: AccountUserTree): string[] {
  return (
    response.account?.properties
      ?.flatMap((property) => property.projects || [])
      .flatMap((project) => project.irrigation_plans || [])
      .flatMap((plan) => plan.steps || [])
      .map((step) => step.id)
      .filter((stepId): stepId is string => Boolean(stepId)) || []
  );
}

function groupStepTanksByStepId(stepTanks: AUTIrrigationPlanStepTank[]) {
  const tanksByStepId = new Map<string, AUTIrrigationPlanStepTank[]>();

  stepTanks.forEach((tank) => {
    const key = String(tank.irrigation_plan_step);
    const existing = tanksByStepId.get(key);
    if (existing) {
      existing.push(tank);
    } else {
      tanksByStepId.set(key, [tank]);
    }
  });

  return tanksByStepId;
}

function attachStepTanksToTree(
  response: AccountUserTree,
  tanksByStepId: Map<string, AUTIrrigationPlanStepTank[]>
) {
  response.account?.properties?.forEach((property) => {
    property.projects?.forEach((project) => {
      project.irrigation_plans?.forEach((plan) => {
        plan.steps?.forEach((step) => {
          step.irrigation_plan_step_tanks = tanksByStepId.get(step.id) || [];
        });
      });
    });
  });
}

// Verification: These types should now resolve correctly
// type TestAUTPropertyAccount = AUTProperty["account"]; // Should be string
// type TestAUTPropertyPoint = AUTProperty["point"]; // Should be Point | null
// type TestAUTWaterPumpProperty = AUTWaterPump["property"]; // Should be string
// type TestAUTWaterPumpType = AUTWaterPump["pump_type"]; // Should be WaterPumpType
// type TestAUTReservoirLocation = AUTReservoir["location"]; // Should be Point | null
// type TestAUTSectorPolygon = AUTSector["polygon"]; // Should be Polygon | null

export type AccountUserWithAccount = Awaited<ReturnType<typeof listAccountsWithOwners>>[number];

/**
 *  List all accounts that the logged user has access to.
 * @param directus
 * @param userMemberId
 * @returns
 */
export async function listAccountsWithOwners(directus: AppDirectusClient) {
  const response = await directus.request(
    readItems("account_user", {
      filter: { user: { _eq: "$CURRENT_USER" } },
      fields: [
        "id",
        "role",
        "start_date",
        "end_date",
        {
          account: [
            "id",
            {
              owner: ["id", "first_name", "last_name", "email", "avatar", "status"],
            },
          ],
        },
      ],
    })
  );
  return response;
}

export async function getAccountUserTree(directus: AppDirectusClient, accountUserId: string) {
  const response = await directus.request(
    readItem("account_user", accountUserId, {
      fields: [
        "id",
        "role",
        "start_date",
        "end_date",
        "metadata",
        "notes",
        {
          account: [
            "id",
            "metadata",
            "notes",
            {
              owner: ["id", "first_name", "last_name", "email", "avatar", "status"],
              properties: [
                "id",
                "account",
                "name",
                "timezone",
                "point",
                "address_postal_code",
                "address_street_name",
                "address_street_number",
                "address_complement",
                "address_neighborhood",
                "address_city",
                "address_state",
                "address_country",
                "backwash_duration_minutes",
                "backwash_period_minutes",
                "backwash_delay_seconds",
                "rain_gauge_enabled",
                "rain_gauge_resolution_mm",
                "precipitation_volume_limit_mm",
                "precipitation_suspended_duration_hours",
                "notes",
                "metadata",
                {
                  water_pumps: [
                    "id",
                    "property",
                    // "water_pump_controller",
                    "label",
                    "identifier",
                    "pump_type",
                    "pump_model",
                    // "has_frequency_inverter",
                    // "monitor_operation",
                    "flow_rate_lh",
                    // "mode",
                    "notes",
                    "metadata",
                  ],
                  reservoirs: [
                    "id",
                    "property",
                    "name",
                    // "reservoir_monitor",
                    "safety_time_minutes",
                    "water_pump",
                    "description",
                    "capacity",
                    "location",
                    "enabled",
                    "notes",
                    "metadata",
                  ],
                  tanks: [
                    "id",
                    "property",
                    "label",
                    "volume_liters",
                    "concentration_g_l",
                    "notes",
                    "metadata",
                    "date_created",
                    {
                      nutrient: ["id", "name"],
                    },
                  ],

                  projects: [
                    "id",
                    "property",
                    "name",
                    "irrigation_water_pump",
                    "fertigation_water_pump",
                    "localized_irrigation_controller",
                    "description",
                    "pipe_wash_time_seconds",
                    "backwash_duration_seconds",
                    "backwash_period_seconds",
                    // "backwash_pump_type",
                    "start_date",
                    "end_date",
                    "metadata",
                    "notes",
                    {
                      irrigation_plans: [
                        "id",
                        "project",
                        "name",
                        "description",
                        "start_time",
                        "days_of_week",
                        "is_enabled",
                        "fertigation_enabled",
                        "backwash_enabled",
                        "total_irrigation_duration",
                        "start_date",
                        "end_date",
                        "metadata",
                        "notes",
                        {
                          steps: [
                            "id",
                            "irrigation_plan",
                            "sector",
                            "description",
                            "order",
                            "duration_seconds",
                            "fertigation_start_delay_seconds",
                            "fertigation_duration_seconds",
                            "notes",
                            "metadata",
                            {
                              sector_mirrors: [
                                "id",
                                "source_irrigation_plan_step",
                                {
                                  target_sector: ["id", "name"],
                                },
                              ],
                              irrigation_plan_step_tanks: [
                                "id",
                                "irrigation_plan_step",
                                "time_minutes",
                                "notes",
                                "metadata",
                                {
                                  project_tank: [
                                    "id",
                                    "flow_rate_lh",
                                    "start_date",
                                    "end_date",
                                    {
                                      tank: [
                                        "id",
                                        "label",
                                        "volume_liters",
                                        "concentration_g_l",
                                        {
                                          nutrient: ["id", "name"],
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                      sectors: [
                        "id",
                        "project",
                        "name",
                        "description",
                        // "valve_controller",
                        // "valve_controller_output",
                        "area",
                        "polygon",
                        "coordinates",
                        "altitude",
                        "power",
                        "metadata",
                        "notes",
                        {
                          sector_crops: [
                            "id",
                            "sector",
                            "planting_date",
                            "plant_population",
                            "start_date",
                            "end_date",
                            "notes",
                            "metadata",
                            {
                              crop_type: ["id", "name"],
                            },
                          ],
                        },
                      ],
                      project_tanks: [
                        "id",
                        "project",
                        "flow_rate_lh",
                        "start_date",
                        "end_date",
                        "notes",
                        "metadata",
                        {
                          tank: [
                            "id",
                            "label",
                            "volume_liters",
                            "concentration_g_l",
                            {
                              nutrient: ["id", "name"],
                            },
                          ],
                          vdc_configurations: [
                            "id",
                            "device",
                            "mode",
                            "turn_on_physical_output_index",
                            "turn_off_physical_output_index",
                            "equipment_type",
                            "start_date",
                            "end_date",
                          ],
                        },
                      ],
                    },
                  ],
                  devices: [
                    "id",
                    "property",
                    "start_date",
                    "end_date",
                    "metadata",
                    "notes",
                    {
                      device: [
                        "id",
                        "identifier",
                        "metadata",
                        "notes",
                        "user_created",
                        "date_created",
                        "user_updated",
                        "date_updated",
                        {
                          virtual_device_configurations: [
                            "id",
                            "date_created",
                            "user_created",
                            "date_updated",
                            "user_updated",
                            "metadata",
                            "notes",
                            "device",
                            "mode",
                            "turn_on_physical_output_index",
                            "turn_off_physical_output_index",
                            "monitoring_physical_input_index",
                            "monitoring_input_mode",
                            "equipment_type",
                            "water_pump",
                            "sector",
                            "project_backwash",
                            "reservoir",
                            "start_date",
                            "end_date",
                            "date_created",
                            "user_created",
                            "date_updated",
                            "user_updated",
                            "metadata",
                            "notes",
                          ],
                          model: [
                            "id",
                            "date_created",
                            "user_created",
                            "date_updated",
                            "user_updated",
                            "metadata",
                            "notes",
                            "label",
                            "outputs",
                            "inputs",
                          ],
                        },
                      ],
                      current_mesh_device_mapping: [
                        "id",
                        "start_date",
                        "end_date",
                        "mesh_property_device",
                        "lic_property_device",
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    })
  );

  const stepIds = collectAccountStepIds(response as AccountUserTree);

  if (stepIds.length > 0) {
    const stepTanks = (await directus.request(
      readItems("irrigation_plan_step_tank", {
        filter: {
          irrigation_plan_step: {
            _in: stepIds,
          },
        },
        fields: [
          "id",
          "irrigation_plan_step",
          "time_minutes",
          "notes",
          "metadata",
          {
            project_tank: [
              "id",
              "flow_rate_lh",
              "start_date",
              "end_date",
              {
                tank: [
                  "id",
                  "label",
                  "volume_liters",
                  "concentration_g_l",
                  {
                    nutrient: ["id", "name"],
                  },
                ],
              },
            ],
          },
        ],
      })
    )) as AUTIrrigationPlanStepTank[];

    attachStepTanksToTree(response as AccountUserTree, groupStepTanksByStepId(stepTanks));
  }

  return response as AccountUserTree;
}

// Define the actual query result structure based on what the query returns
// The query selects relation fields directly (like "account") which return string IDs
// and relation fields with nested selections (like water_pumps: [...]) which return full objects
type AUTAccountQueryResult = {
  id: string;
  metadata: AnyJson | null;
  notes: string | null;
  owner: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    avatar: string | null;
    status: string;
  };
  properties: Array<{
    id: string;
    account: string; // Direct selection - returns string ID
    name: string;
    timezone: string;
    point: Point | null;
    address_postal_code: string | null;
    address_street_name: string | null;
    address_street_number: string | null;
    address_complement: string | null;
    address_neighborhood: string | null;
    address_city: string | null;
    address_state: string | null;
    address_country: string | null;
    backwash_duration_minutes: number | null;
    backwash_period_minutes: number | null;
    backwash_delay_seconds: number | null;
    rain_gauge_enabled: boolean;
    rain_gauge_resolution_mm: number | null;
    precipitation_volume_limit_mm: number | null;
    precipitation_suspended_duration_hours: number | null;
    notes: string | null;
    metadata: AnyJson | null;
    water_pumps: Array<{
      id: string;
      property: string; // Direct selection - returns string ID
      // water_pump_controller: string; // Direct selection - returns string ID
      label: string;
      identifier: string;
      pump_type: WaterPumpType;
      pump_model: string;
      // has_frequency_inverter: boolean;
      // monitor_operation: boolean;
      flow_rate_lh: number | null;
      // mode: WaterPumpMode;
      notes: string | null;
      metadata: AnyJson | null;
    }>;
    reservoirs: Array<{
      id: string;
      property: string; // Direct selection - returns string ID
      name: string;
      // reservoir_monitor: string; // Direct selection - returns string ID
      water_pump: string; // Direct selection - returns string ID
      description: string | null;
      capacity: number | null;
      safety_time_minutes: number | null;
      location: Point | null;
      enabled: boolean;
      notes: string | null;
      metadata: AnyJson | null;
    }>;
    tanks: Array<{
      id: string;
      property: string; // Direct selection - returns string ID
      label: string;
      volume_liters: number;
      concentration_g_l: number;
      notes: string | null;
      metadata: AnyJson | null;
      date_created: string;
      nutrient: { id: string; name: string }; // Nested selection - returns object
    }>;
    projects: Array<{
      id: string;
      property: string; // Direct selection - returns string ID
      name: string;
      irrigation_water_pump: string | null; // Direct selection - returns string ID or null
      fertigation_water_pump: string; // Direct selection - returns string ID
      localized_irrigation_controller: string | null; // Direct selection - returns string ID or null
      description: string | null;
      pipe_wash_time_seconds: number | null;
      backwash_duration_seconds: number | null;
      backwash_period_seconds: number | null;
      // backwash_pump_type: "IRRIGATION" | "FERTIGATION" | null;
      start_date: string | null;
      end_date: string | null;
      metadata: AnyJson | null;
      notes: string | null;
      irrigation_plans: Array<{
        id: string;
        project: string; // Direct selection - returns string ID
        name: string;
        description: string | null;
        start_time: string;
        days_of_week: DayOfWeek[];
        is_enabled: boolean;
        fertigation_enabled: boolean;
        backwash_enabled: boolean;
        total_irrigation_duration: number;
        start_date: string | null;
        end_date: string | null;
        metadata: AnyJson | null;
        notes: string | null;
        steps: Array<{
          id: string;
          irrigation_plan: string; // Direct selection - returns string ID
          sector: string; // Direct selection - returns string ID
          description: string | null;
          order: number;
          duration_seconds: number;
          fertigation_start_delay_seconds: number | null;
          fertigation_duration_seconds: number | null;
          notes: string | null;
          metadata: AnyJson | null;
          sector_mirrors: Array<{
            id: string;
            source_irrigation_plan_step: string;
            target_sector: { id: string; name: string };
          }>;
          irrigation_plan_step_tanks: Array<{
            id: string;
            irrigation_plan_step: string; // Direct selection - returns string ID
            time_minutes: number;
            notes: string | null;
            metadata: AnyJson | null;
            project_tank: {
              // Nested selection - returns object
              id: string;
              flow_rate_lh: number;
              start_date: string;
              end_date: string | null;
              tank: {
                id: string;
                label: string;
                volume_liters: number;
                concentration_g_l: number;
                nutrient: { id: string; name: string };
              };
            };
          }>;
        }>;
      }>;
      sectors: Array<{
        id: string;
        project: string; // Direct selection - returns string ID
        name: string;
        description: string | null;
        // valve_controller: string | null; // Direct selection - returns string ID or null
        // valve_controller_output: ValveControllerOutput | null;
        area: number | null;
        polygon: Polygon | null;
        coordinates: Point | null;
        altitude: number | null;
        power: number;
        metadata: AnyJson | null;
        notes: string | null;
        sector_crops: Array<{
          id: string;
          sector: string; // Direct selection - returns string ID
          planting_date: string;
          plant_population: number | null;
          start_date: string;
          end_date: string | null;
          notes: string | null;
          metadata: AnyJson | null;
          crop_type: { id: string; name: string }; // Nested selection - returns object
        }>;
      }>;
      project_tanks: Array<{
        id: string;
        project: string; // Direct selection - returns string ID
        flow_rate_lh: number;
        start_date: string;
        end_date: string | null;
        notes: string | null;
        metadata: AnyJson | null;
        tank: {
          // Nested selection - returns object
          id: string;
          label: string;
          volume_liters: number;
          concentration_g_l: number;
          nutrient: { id: string; name: string };
        };
        vdc_configurations: Array<{
          id: string;
          device: string; // Direct selection - returns string ID
          mode: string;
          turn_on_physical_output_index: number;
          turn_off_physical_output_index: number | null;
          equipment_type: string;
          start_date: string;
          end_date: string | null;
        }>;
      }>;
    }>;
    devices: Array<{
      id: string;
      property: string; // Direct selection - returns string ID
      start_date: string;
      end_date: string | null;
      metadata: AnyJson | null;
      notes: string | null;
      device: {
        id: string;
        identifier: string;
        model: DeviceModel;
        metadata: AnyJson | null;
        notes: string | null;
        user_created: string | null;
        virtual_device_configurations: VirtualDeviceConfiguration[];
      };
      current_mesh_device_mapping: {
        id: string;
        start_date: string;
        end_date: string | null;
        mesh_property_device: string; // Direct selection - returns string ID
        lic_property_device: string; // Direct selection - returns string ID
      };
    }>;
  }>;
};

export type AUTAccountRaw = AUTAccountQueryResult;
export type AUTPropertyRaw = NonNullable<AUTAccountRaw["properties"]>[number];
export type AUTPropertyDeviceRaw = NonNullable<AUTPropertyRaw["devices"]>[number];
export type AUTDeviceRaw = NonNullable<AUTPropertyDeviceRaw["device"]>;
export type AUTCurrentMeshDeviceMappingRaw = NonNullable<AUTPropertyDeviceRaw["current_mesh_device_mapping"]>;
export type AUTWaterPumpRaw = NonNullable<AUTPropertyRaw["water_pumps"]>[number];
export type AUTReservoirRaw = NonNullable<AUTPropertyRaw["reservoirs"]>[number];
export type AUTTankRaw = NonNullable<AUTPropertyRaw["tanks"]>[number];
export type AUTProjectRaw = NonNullable<AUTPropertyRaw["projects"]>[number];
export type AUTProjectTankRaw = NonNullable<AUTProjectRaw["project_tanks"]>[number];
export type AUTProjectTankVdcConfigurationRaw = NonNullable<AUTProjectTankRaw["vdc_configurations"]>[number];
export type AUTSectorRaw = NonNullable<AUTProjectRaw["sectors"]>[number];
export type AUTSectorCropRaw = NonNullable<AUTSectorRaw["sector_crops"]>[number];
export type AUTIrrigationPlanRaw = NonNullable<AUTProjectRaw["irrigation_plans"]>[number];
export type AUTPropertyIrrigationPlanStepRaw = NonNullable<AUTIrrigationPlanRaw["steps"]>[number];
export type AUTIrrigationPlanStepTankRaw = NonNullable<AUTPropertyIrrigationPlanStepRaw["irrigation_plan_step_tanks"]>[number];

// Verification: All types now resolve correctly with proper TypeScript types
// Relation fields: AUTProperty["account"] -> string, AUTWaterPump["property"] -> string
// Non-relation fields: AUTProperty["point"] -> Point | null, AUTReservoir["location"] -> Point | null

// Define leaf types first (no child arrays to override)
export type AUTDevice = AUTDeviceRaw;

export type AUTCurrentMeshDeviceMapping = OmitAndMerge<
  AUTCurrentMeshDeviceMappingRaw,
  "mesh_property_device" | "lic_property_device",
  {
    mesh_property_device: string;
    lic_property_device: string;
  }
>;

export type AUTPropertyIrrigationPlanStep = OmitAndMerge<
  AUTPropertyIrrigationPlanStepRaw,
  "irrigation_plan" | "sector" | "irrigation_plan_step_tanks",
  {
    irrigation_plan: string;
    sector: string;
    irrigation_plan_step_tanks: AUTIrrigationPlanStepTank[];
  }
>;

export type AUTWaterPump = OmitAndMerge<
  AUTWaterPumpRaw,
  "property",
  {
    property: string;
    // water_pump_controller: string | null;
  }
>;

export type AUTReservoir = OmitAndMerge<
  AUTReservoirRaw,
  "property" | "water_pump",
  {
    property: string;
    // reservoir_monitor: string;
    water_pump: string;
  }
>;

export type AUTTank = AUTTankRaw;

export type AUTProjectTankVdcConfiguration = OmitAndMerge<
  AUTProjectTankVdcConfigurationRaw,
  "device",
  {
    device: string;
  }
>;

export type AUTProjectTank = OmitAndMerge<
  AUTProjectTankRaw,
  "project" | "vdc_configurations",
  {
    project: string;
    vdc_configurations: AUTProjectTankVdcConfiguration[];
  }
>;

export type AUTSectorCrop = AUTSectorCropRaw;

export type AUTIrrigationPlanStepTank = OmitAndMerge<
  AUTIrrigationPlanStepTankRaw,
  "irrigation_plan_step",
  {
    irrigation_plan_step: string;
  }
>;
export type AUTSector = OmitAndMerge<
  AUTSectorRaw,
  "project" | "sector_crops",
  {
    project: string;
    // valve_controller: string | null;
    sector_crops: AUTSectorCrop[];
  }
>;

// Define types with child arrays that need to reference refined child types
export type AUTIrrigationPlan = OmitAndMerge<
  AUTIrrigationPlanRaw,
  "project" | "steps",
  {
    project: string;
    steps: AUTPropertyIrrigationPlanStep[];
  }
>;

export type AUTProject = OmitAndMerge<
  AUTProjectRaw,
  | "property"
  | "irrigation_water_pump"
  | "fertigation_water_pump"
  | "localized_irrigation_controller"
  | "irrigation_plans"
  | "sectors"
  | "project_tanks",
  {
    property: string;
    irrigation_water_pump: string | null;
    fertigation_water_pump: string;
    localized_irrigation_controller: string | null;
    irrigation_plans: AUTIrrigationPlan[];
    sectors: AUTSector[];
    project_tanks: AUTProjectTank[];
  }
>;

export type AUTPropertyDevice = OmitAndMerge<
  AUTPropertyDeviceRaw,
  "property" | "device" | "current_mesh_device_mapping",
  {
    property: string;
    device: AUTDevice;
    current_mesh_device_mapping: AUTCurrentMeshDeviceMapping;
  }
>;

export type AUTProperty = OmitAndMerge<
  AUTPropertyRaw,
  "account" | "water_pumps" | "reservoirs" | "tanks" | "projects" | "devices",
  {
    account: string;
    water_pumps: AUTWaterPump[];
    reservoirs: AUTReservoir[];
    tanks: AUTTank[];
    projects: AUTProject[];
    devices: AUTPropertyDevice[];
  }
>;

export type AUTAccount = OmitAndMerge<
  AUTAccountRaw,
  "properties",
  {
    properties: AUTProperty[];
  }
>;

export type AccountUserTree = {
  id: string;
  role: string;
  start_date: string;
  end_date: string | null;
  metadata: AnyJson | null;
  notes: string | null;
  account: AUTAccount;
};
